import React, { useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  Platform,
  Modal,
  Pressable,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useQuery } from "@tanstack/react-query";
import { BlurView } from "expo-blur";
import { router } from "expo-router";
import { FontFamilies } from "@/constants/Typography";
import { Colors } from "@/constants/Colors";
import { useChat, ChatMessage } from "@/hooks/useChat";
import {
  AudioPlayerWidget,
  ActiveCallWaveform,
} from "@/components/chat/ChatComponents";
import RefundProcessServices from "@/services/refundProcessServices";
import { Shadows } from "@/constants/Shadows";
import { StyleSheet } from "@/utils/appStyleSheet";

// Parse text with **bold** markdown syntax
const parseMessageText = (
  text: string,
  baseStyle: object,
  boldStyle: object,
): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      return (
        <Text key={index} style={[baseStyle, boldStyle]}>
          {boldText}
        </Text>
      );
    }
    return part;
  });
};

// Suggestion prompts
const SUGGESTION_PROMPTS = [
  "Cancel my Netflix subscription",
  "Negotiate my hotel booking",
  "Request a discount on my Target order",
];

interface ChatPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

export default function ChatPopup({ isVisible, onClose }: ChatPopupProps) {
  const scrollViewRef = useRef<ScrollView>(null);

  // Check if user has linked emails
  const { data: linkedEmails } = useQuery({
    queryKey: ["linkedEmails"],
    queryFn: RefundProcessServices.getLinkedEmail,
    enabled: isVisible,
  });

  const hasLinkedEmail = linkedEmails && linkedEmails.length > 0;

  const {
    inputText,
    setInputText,
    messages,
    chatSessionId,
    isLoading,
    isInitializing,
    isPollingCall,
    isAITyping,
    isAudioConnected,
    isAudioPlaying,
    inputRef,
    initializeSession,
    sendMessage,
    cleanup,
  } = useChat({});

  const handleLinkEmail = () => {
    const sessionId = chatSessionId ?? "";
    cleanup();
    onClose();
    router.push({
      pathname: "/account/linked-emails",
      params: {
        returnTo: "chat",
        chatSessionId: sessionId,
      },
    });
  };

  // Initialize session when popup opens
  useEffect(() => {
    if (isVisible) {
      initializeSession();
    }
  }, [isVisible, initializeSession]);

  // Scroll to bottom when messages change
  const lastMessage = messages[messages.length - 1];
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages.length, lastMessage?.text]);

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const handleSuggestionPress = (suggestion: string) => {
    setInputText(suggestion);
    setTimeout(() => {
      sendMessage();
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const handleSend = () => {
    sendMessage();
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  // Handle key press for Enter to send
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS === "web") {
        const nativeEvent = e.nativeEvent as TextInputKeyPressEventData & {
          ctrlKey?: boolean;
          shiftKey?: boolean;
          metaKey?: boolean;
        };
        if (nativeEvent.key === "Enter") {
          if (
            nativeEvent.ctrlKey ||
            nativeEvent.shiftKey ||
            nativeEvent.metaKey
          ) {
            return;
          }
          e.preventDefault();
          if (inputText.trim() && !isLoading && !isInitializing) {
            handleSend();
          }
        }
      }
    },
    [inputText, isLoading, isInitializing, handleSend],
  );

  // Check if message should show link email button
  const shouldShowLinkEmail = (message: ChatMessage): boolean => {
    if (message.isUser) return false;
    return message.action === "link_email";
  };

  const renderMessage = (message: ChatMessage) => {
    // Render audio player widget for call recording
    if (message.type === "call_audio" && message.audioUrl) {
      return (
        <AudioPlayerWidget
          key={message.id}
          audioUrl={message.audioUrl}
          duration={message.audioDuration}
        />
      );
    }

    const showLinkEmail = shouldShowLinkEmail(message);

    if (showLinkEmail) {
      return (
        <View
          key={message.id}
          style={[styles.messageBubble, styles.botMessage]}
        >
          <Text style={[styles.messageText, styles.botMessageText]}>
            {parseMessageText(
              message.text,
              styles.botMessageText,
              styles.boldText,
            )}
          </Text>
          <TouchableOpacity
            style={styles.actionLink}
            onPress={handleLinkEmail}
            activeOpacity={0.7}
          >
            <Text style={styles.actionLinkText}>Link Gmail account</Text>
            <Ionicons name="chevron-forward" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      );
    }

    const textStyle = message.isUser
      ? styles.messageText
      : [styles.messageText, styles.botMessageText];

    return (
      <View
        key={message.id}
        style={[
          styles.messageBubble,
          message.isUser ? styles.userMessage : styles.botMessage,
        ]}
      >
        <Text style={textStyle}>
          {parseMessageText(
            message.text,
            message.isUser ? styles.messageText : styles.botMessageText,
            styles.boldText,
          )}
        </Text>
      </View>
    );
  };

  const hasMessages = messages.length > 0;

  if (!isVisible) return null;

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable style={styles.overlay} onPress={handleClose}>
        <Pressable
          style={styles.popupContainer}
          onPress={(e) => e.stopPropagation()}
        >
          <BlurView intensity={80} tint="light" style={styles.blurContainer}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <View style={styles.statusDot} />
                <Text style={styles.headerTitle}>Start AI refund</Text>
              </View>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={handleClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="close" size={24} color="#161D14" />
              </TouchableOpacity>
            </View>

            {/* Messages Area */}
            <ScrollView
              ref={scrollViewRef}
              style={styles.messagesContainer}
              contentContainerStyle={styles.messagesContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {messages.map(renderMessage)}
              {(isLoading || isAITyping) && (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="small" color={Colors.primary} />
                </View>
              )}
            </ScrollView>

            {/* Suggestion Chips - Only show when no messages */}
            {!hasMessages && !isPollingCall && (
              <View style={styles.suggestionsContainer}>
                {SUGGESTION_PROMPTS.map((suggestion, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.suggestionChip}
                    onPress={() => handleSuggestionPress(suggestion)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.suggestionText}>{suggestion}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Input Area */}
            <View style={styles.inputWrapper}>
              {isPollingCall ? (
                <ActiveCallWaveform
                  isStreaming={isAudioConnected || isAudioPlaying}
                />
              ) : (
                <View style={styles.inputContainer}>
                  <TextInput
                    ref={inputRef}
                    style={styles.textInput}
                    placeholder="Help me get a refund for..."
                    placeholderTextColor="#A3A3A3"
                    value={inputText}
                    onChangeText={setInputText}
                    maxLength={500}
                    returnKeyType="send"
                    blurOnSubmit={false}
                    onSubmitEditing={handleSend}
                    onKeyPress={handleKeyPress}
                    editable={!isInitializing && !isLoading}
                    multiline={true}
                    selectionColor={Colors.primary}
                    underlineColorAndroid="transparent"
                  />
                  <TouchableOpacity
                    style={[
                      styles.sendButton,
                      inputText.trim() && !isLoading
                        ? styles.sendButtonActive
                        : styles.sendButtonInactive,
                    ]}
                    onPress={handleSend}
                    disabled={!inputText.trim() || isLoading || isInitializing}
                  >
                    {isLoading ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Ionicons
                        name="arrow-up"
                        size={20}
                        color={inputText.trim() ? "#FFFFFF" : "#00000040"}
                      />
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </BlurView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    padding: 24,
  },
  popupContainer: {
    width: 360,
    height: 500,
    borderRadius: 16,
    overflow: "hidden",
    ...Shadows.medium,
  },
  blurContainer: {
    flex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(22, 29, 20, 0.1)",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.primary,
  },
  headerTitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 23,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    flexGrow: 1,
    paddingHorizontal: 10,
    paddingTop: 16,
    gap: 8,
  },
  loadingContainer: {
    paddingVertical: 12,
    alignItems: "flex-start",
  },
  messageBubble: {
    maxWidth: "85%",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flexShrink: 1,
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: "#FFFFFF",
  },
  botMessage: {
    backgroundColor: Colors.primary,
    alignSelf: "flex-start",
  },
  messageText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    lineHeight: 23,
    flexShrink: 1,
    flexWrap: "wrap",
  },
  botMessageText: {
    color: "#FFFFFF",
  },
  boldText: {
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  actionLink: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.3)",
  },
  actionLinkText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#FFFFFF",
    flex: 1,
  },
  suggestionsContainer: {
    paddingHorizontal: 10,
    paddingBottom: 8,
    gap: 8,
  },
  suggestionChip: {
    backgroundColor: "#F2FFF5",
    borderWidth: 1,
    borderColor: "rgba(15, 145, 41, 0.2)",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  suggestionText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#0E3115",
    textAlign: "center",
    lineHeight: 17,
    letterSpacing: -0.26,
  },
  inputWrapper: {
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 12,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
    minHeight: 64,
    ...Shadows.light,
  },
  textInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    maxHeight: 80,
    paddingVertical: 8,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  sendButtonActive: {
    backgroundColor: Colors.primary,
  },
  sendButtonInactive: {
    backgroundColor: "#E5E5E5",
  },
});
